import type {
  AdminEligibilityPreviewPreliminaryEstimate,
  AdminEligibilityPreviewProduct,
  Cart,
  Quote,
  ResellerEntitlement,
} from './commerce';
import type { SalesWorkspaceContext } from './sales-collaboration';

const salesRequestWizardDraftStoragePrefix = 'sales-request-wizard:';
const salesRequestWizardDraftFallbackStore = new Map<string, SalesRequestWizardDraft>();

export interface SalesRoutePreviewAccessPoint {
  id: string;
  name?: string;
  latitude: number;
  longitude: number;
  metadata?: Record<string, unknown>;
}

export interface SalesRoutePreviewGeometry {
  type: 'LineString' | 'MultiLineString';
  coordinates: unknown;
}

export interface SalesRoutePreviewRequest {
  latitude: number;
  longitude: number;
  address?: string;
  routing_profile?: 'standard' | 'fiber_construction';
  build_type?: string;
  catalog_owner_tenant_id?: string;
  entitlement_id?: string;
  metadata?: Record<string, unknown>;
}

export interface CreateSalesMapQuoteLocationInput {
  feature_identity_key: string;
  location_id?: string;
  display_name?: string;
  address_line1?: string;
  locality?: string;
  region_code?: string;
  postal_code?: string;
  occupancy_kind?: string;
  business_name?: string;
  latitude: number;
  longitude: number;
  metadata?: Record<string, unknown>;
}

export interface CreateSalesMapQuoteInput {
  location: CreateSalesMapQuoteLocationInput;
  route_preview: SalesRoutePreviewResponse;
}

export interface SalesRequestWizardDraft {
  location: CreateSalesMapQuoteLocationInput;
  sales_context?: SalesWorkspaceContext | null;
  route_preview?: {
    origin_approach_distance_ft: number;
    origin_approach_distance_m: number;
    road_distance_ft: number;
    road_distance_m: number;
    destination_approach_distance_ft: number;
    destination_approach_distance_m: number;
    total_build_distance_ft: number;
    total_build_distance_m: number;
    nearest_fiber_access_point_id?: string;
    nearest_fiber_access_point?: SalesRoutePreviewAccessPoint | null;
    distance_quality?: string;
    source?: string;
    routing_profile?: string;
    route?: SalesRoutePreviewResponse['route'];
    route_geometry?: SalesRoutePreviewGeometry | null;
    preliminary_estimate?: AdminEligibilityPreviewPreliminaryEstimate | null;
    build_type?: string | null;
    metadata?: Record<string, unknown> | null;
  };
}

function salesRequestWizardDraftStorageKey(draftId: string): string {
  return `${salesRequestWizardDraftStoragePrefix}${draftId}`;
}

function createSalesRequestWizardDraftID(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  return `draft-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function readSalesRequestWizardDraftByID(draftId: string): SalesRequestWizardDraft | null {
  const trimmedDraftID = draftId.trim();
  if (!trimmedDraftID) {
    return null;
  }
  const storageKey = salesRequestWizardDraftStorageKey(trimmedDraftID);
  const inMemoryDraft = salesRequestWizardDraftFallbackStore.get(trimmedDraftID) ?? null;
  if (typeof window === 'undefined') {
    return inMemoryDraft;
  }
  try {
    const encoded = window.sessionStorage.getItem(storageKey);
    if (!encoded) {
      return inMemoryDraft;
    }
    const parsed = JSON.parse(encoded) as SalesRequestWizardDraft;
    if (!parsed?.location?.feature_identity_key) {
      return null;
    }
    salesRequestWizardDraftFallbackStore.set(trimmedDraftID, parsed);
    return parsed;
  } catch {
    return inMemoryDraft;
  }
}

function persistSalesRequestWizardDraft(draftId: string, payload: SalesRequestWizardDraft): void {
  const trimmedDraftID = draftId.trim();
  if (!trimmedDraftID) {
    throw new Error('Sales request draft id is required.');
  }
  salesRequestWizardDraftFallbackStore.set(trimmedDraftID, payload);
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.sessionStorage.setItem(salesRequestWizardDraftStorageKey(trimmedDraftID), JSON.stringify(payload));
  } catch {
    // Keep the in-memory fallback so same-tab SPA navigation still works when storage is unavailable.
  }
}

export interface SalesRequestServiceSelection {
  kind: string;
  product_id: string;
  variant_id: string;
  offer_id?: string;
  quantity: number;
}

export interface SalesRequestServiceOption {
  kind: string;
  product_id: string;
  variant_id: string;
  offer_id?: string | null;
  product_name: string;
  display_name: string;
  description?: string;
  class?: string;
  currency?: string;
  price?: number;
  discounted_price?: number;
  billing_period_months?: number;
  charge_type?: string;
  limit_per_cart?: number;
  offer_display_name?: string;
}

export interface SalesRequestEvaluation {
  entitlement: ResellerEntitlement;
  evaluation: {
    request_id?: string;
    status: string;
    direct_checkout_allowed: boolean;
    quote_required: boolean;
    available_product_ids: string[];
    sellable_products?: AdminEligibilityPreviewProduct[];
    blocked_products: Array<{
      product_id: string;
      display_name: string;
      reasons: string[];
      customer_hint?: string | null;
      blocked_variants: Array<{
        variant_id: string;
        display_name: string;
        reasons: string[];
      }>;
    }>;
    customer_message?: string | null;
    eligibility_summary?: Record<string, unknown>;
    preliminary_estimate?: AdminEligibilityPreviewPreliminaryEstimate | null;
  };
  sellable_products?: AdminEligibilityPreviewProduct[];
  sellable_service_options: SalesRequestServiceOption[];
  recommended_action: 'quote' | 'cart' | 'blocked';
}

export interface EvaluateSalesRequestInput {
  entitlement_id: string;
  catalog_owner_tenant_id?: string;
  store_id?: string;
  location: CreateSalesMapQuoteLocationInput;
  route_preview?: SalesRequestWizardDraft['route_preview'];
}

export interface CreateSalesRequestQuoteInput extends EvaluateSalesRequestInput {
  customer_snapshot?: Record<string, unknown>;
  interested_line_items: SalesRequestServiceSelection[];
  metadata?: Record<string, unknown>;
}

export interface CreateSalesRequestCartInput extends EvaluateSalesRequestInput {
  interested_line_items: SalesRequestServiceSelection[];
  metadata?: Record<string, unknown>;
}

export interface SalesRoutePreviewResponse {
  origin_approach_distance_ft: number;
  origin_approach_distance_m: number;
  road_distance_ft: number;
  road_distance_m: number;
  destination_approach_distance_ft: number;
  destination_approach_distance_m: number;
  total_build_distance_ft: number;
  total_build_distance_m: number;
  nearest_fiber_access_point_id?: string;
  nearest_fiber_access_point?: SalesRoutePreviewAccessPoint | null;
  distance_quality: string;
  source: string;
  routing_profile?: string;
  route_geometry?: SalesRoutePreviewGeometry | null;
  route?: {
    distance_m?: number;
    distance_ft?: number;
    origin_approach_distance_m?: number;
    origin_approach_distance_ft?: number;
    road_distance_m?: number;
    road_distance_ft?: number;
    destination_approach_distance_m?: number;
    destination_approach_distance_ft?: number;
    total_build_distance_m?: number;
    total_build_distance_ft?: number;
    routing_profile?: string;
    routing_cost_m?: number;
    routing_cost_ft?: number;
    duration_seconds?: number;
    geometry?: SalesRoutePreviewGeometry | null;
    intersects_railroad?: boolean;
    candidate_access_point_id?: string;
  } | null;
  preliminary_estimate?: AdminEligibilityPreviewPreliminaryEstimate | null;
  metadata?: Record<string, unknown> | null;
}

export async function previewSalesRoute(payload: SalesRoutePreviewRequest): Promise<SalesRoutePreviewResponse> {
  const response = await fetch('/api/v1/sales/route-preview', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const message = await response.text().catch(() => '');
    throw new Error(message || `Route preview failed with status ${response.status}`);
  }
  return (await response.json()) as SalesRoutePreviewResponse;
}

export async function createSalesMapQuote(payload: CreateSalesMapQuoteInput) {
  const response = await fetch('/api/v1/sales/quotes', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const message = await response.text().catch(() => '');
    throw new Error(message || `Quote creation failed with status ${response.status}`);
  }
  return (await response.json()) as { quote_id: string };
}

export function buildSalesRequestWizardRoute(payload: SalesRequestWizardDraft): string {
  const draftID = createSalesRequestWizardDraftID();
  persistSalesRequestWizardDraft(draftID, payload);
  return `/sales/requests/new?draft_id=${encodeURIComponent(draftID)}`;
}

export function parseSalesRequestWizardDraft(path: string | null | undefined): SalesRequestWizardDraft | null {
  const source = typeof path === 'string' && path.trim()
    ? path
    : typeof window !== 'undefined'
      ? `${window.location.pathname}${window.location.search}`
      : '';
  const search = source.includes('?') ? source.slice(source.indexOf('?')) : '';
  const params = new URLSearchParams(search);
  const draftID = params.get('draft_id');
  if (draftID) {
    return readSalesRequestWizardDraftByID(draftID);
  }
  const encoded = params.get('draft');
  if (!encoded) {
    return null;
  }
  try {
    const parsed = JSON.parse(decodeURIComponent(encoded)) as SalesRequestWizardDraft;
    if (!parsed?.location?.feature_identity_key) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearSalesRequestWizardDraft(path: string | null | undefined): void {
  const source = typeof path === 'string' && path.trim()
    ? path
    : typeof window !== 'undefined'
      ? `${window.location.pathname}${window.location.search}`
      : '';
  const search = source.includes('?') ? source.slice(source.indexOf('?')) : '';
  const draftID = new URLSearchParams(search).get('draft_id');
  if (!draftID) {
    return;
  }
  salesRequestWizardDraftFallbackStore.delete(draftID);
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.sessionStorage.removeItem(salesRequestWizardDraftStorageKey(draftID));
  } catch {
    // ignore storage cleanup failures
  }
}

export async function evaluateSalesRequest(payload: EvaluateSalesRequestInput): Promise<SalesRequestEvaluation> {
  const response = await fetch('/api/v1/sales/requests/evaluate', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const message = await response.text().catch(() => '');
    throw new Error(message || `Sales request evaluation failed with status ${response.status}`);
  }
  return (await response.json()) as SalesRequestEvaluation;
}

export async function createSalesRequestQuote(payload: CreateSalesRequestQuoteInput): Promise<Quote> {
  const response = await fetch('/api/v1/sales/requests/quotes', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const message = await response.text().catch(() => '');
    throw new Error(message || `Sales request quote creation failed with status ${response.status}`);
  }
  return (await response.json()) as Quote;
}

export async function createSalesRequestCart(payload: CreateSalesRequestCartInput): Promise<Cart> {
  const response = await fetch('/api/v1/sales/requests/carts', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const message = await response.text().catch(() => '');
    throw new Error(message || `Sales request cart creation failed with status ${response.status}`);
  }
  return (await response.json()) as Cart;
}
